import React, { useRef, useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import INDUSTRY_VIDEO from '../../../app/assets/images/homepage/industryvideo.mp4'

interface IndustryItem {
  number: string
  title: string
}

interface IndustriesSectionData {
  title?: string
  subTitle?: string
  industries?: IndustryItem[]
}

interface IndustriesSectionProps {
  data?: IndustriesSectionData
}

const IndustriesSection = ({ data }: IndustriesSectionProps) => {
  const {
    title = 'INDUSTRIES',
    subTitle = 'Deep context across six high automation verticals.',
    industries = [
      { number: '01', title: 'Fintech & Healthcare' },
      { number: '02', title: 'SaaS & Dev Tooling' },
      { number: '03', title: 'Web3 & DeFi & Payments' },
      { number: '04', title: 'Operations & RevOps' },
      { number: '05', title: 'E-commerce & D2C' },
      { number: '06', title: 'Professional Services' },
    ],
  } = data || {}

  const sectionRef = useRef<HTMLElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const line1Ref = useRef<HTMLDivElement>(null)
  const line2Ref = useRef<HTMLDivElement>(null)
  const line3Ref = useRef<HTMLDivElement>(null)

  // Measure grid position relative to section and stretch lines to full section height
  useEffect(() => {
    const updateLines = () => {
      const section = sectionRef.current
      const grid = gridRef.current
      if (!section || !grid) return

      const sectionRect = section.getBoundingClientRect()
      const gridRect = grid.getBoundingClientRect()

      // How far the grid top is from the section top
      const gridOffsetTop = gridRect.top - sectionRect.top
      // How far the grid bottom is from the section bottom
      const gridOffsetBottom = sectionRect.bottom - gridRect.bottom

        // For each line ref, extend it upward/downward to cover full section
        ;[line2Ref, line3Ref].forEach((ref) => {
          if (ref.current) {
            ref.current.style.top = `-${gridOffsetTop}px`
            ref.current.style.bottom = `-${gridOffsetBottom}px`
            ref.current.style.height = 'auto'
          }
        })

      // Line 1 starts at row 2 (1/3 of grid height) and goes to section bottom
      if (line1Ref.current) {
        const row2Top = gridRect.height / 3
        line1Ref.current.style.top = `${row2Top}px`
        line1Ref.current.style.bottom = `-${gridOffsetBottom}px`
        line1Ref.current.style.height = 'auto'
      }
    }

    updateLines()

    const resizeObserver = new ResizeObserver(updateLines)
    if (sectionRef.current) resizeObserver.observe(sectionRef.current)
    if (gridRef.current) resizeObserver.observe(gridRef.current)

    return () => resizeObserver.disconnect()
  }, [])

  const Card = ({ item }: { item: IndustryItem }) => (
    <div className="relative h-[150px] sm:h-[150px] md:h-[200px] lg:h-[200px] xl:h-[260px] 2xl:h-[300px] w-full rounded-[18px] overflow-hidden">
      <div className="absolute inset-0 rounded-[18px]">
        <motion.div
          className="absolute inset-[-100%] rounded-[18px]"
          animate={{ rotate: 360 }}
          transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
          style={{
            background:
              'conic-gradient(from 0deg, transparent 0deg, transparent 150deg, #229CFF 180deg, transparent 210deg, transparent 360deg)',
          }}
        />
      </div>
      <div className="absolute inset-0 rounded-[18px]">
        <motion.div
          className="absolute inset-[-100%] rounded-[18px]"
          animate={{ rotate: 360 }}
          transition={{ duration: 8, repeat: Infinity, ease: 'linear', delay: -4 }}
          style={{
            background:
              'conic-gradient(from 0deg, transparent 0deg, transparent 150deg, rgba(34,156,255,0.7) 180deg, transparent 210deg, transparent 360deg)',
          }}
        />
      </div>
      <div className="absolute inset-[1.5px] rounded-[17px] bg-[#0B121A] p-4 sm:p-5 md:p-6 lg:p-5 xl:p-7">
        <div className="flex h-[45px] w-[45px] sm:h-[50px] sm:w-[50px] xl:h-[70px] xl:w-[70px] 2xl:h-[85px] 2xl:w-[85px] items-center justify-center border border-[#229CFF1A] bg-[#0A0A0A]">
          <span className="text-[14px] sm:text-[16px] md:text-[18px] xl:text-[20px] font-semibold text-[#229CFF]">
            {item.number}
          </span>
        </div>
        <h3 className="pb-[10%] absolute bottom-4 left-4 sm:bottom-5 sm:left-5 md:bottom-6 md:left-6 lg:bottom-5 lg:left-5 xl:bottom-7 xl:left-7 max-w-[140px] sm:max-w-[200px] xl:max-w-[250px] text-[15px] sm:text-[14px] md:text-[20px] lg:text-[23px] xl:text-[30px] 2xl:text-[32px] font-medium leading-[1.15] text-white">
          {item.title}
        </h3>
      </div>
    </div>
  )

  return (
    <section ref={sectionRef} className="relative overflow-hidden py-14 lg:py-24 bg-[#050B12]">
      <video
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 h-full w-full object-cover"
        style={{ opacity: 0.5 }}
      >
        <source src={INDUSTRY_VIDEO} type="video/mp4" />
      </video>

      {/* BLUE GLOW */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(34,156,255,0.16),transparent_65%)]" />

      <div className="relative z-10 mx-auto max-w-[2000px] px-4 sm:px-6 md:px-10 lg:px-14 xl:px-20">

        {/* ===================================================
            DESKTOP & TABLET (sm and up) — Grid with lines inside
        =================================================== */}
        <div
          ref={gridRef}
          className="hidden sm:grid min-h-[600px] lg:min-h-[720px] xl:min-h-[900px]"
          style={{
            gridTemplateColumns: '1.1fr 1px 1fr 1px 1fr 1px 1fr',
            gridTemplateRows: '1fr 1fr 1fr',
          }}
        >
          {/* ── HEADER ── */}
          <div
            className="flex flex-col justify-start pt-2 pr-6 lg:pr-8 xl:pr-10"
            style={{ gridColumn: '1 / 4', gridRow: '1' }}
          >
            <div className="text-[#229CFF] text-xs sm:text-sm xl:text-base tracking-[0.28em] uppercase font-semibold mb-2 lg:mb-3">
              {title}
            </div>
            <div className="font-medium text-white text-[22px] sm:text-[26px] md:text-[25px] lg:text-[32px] xl:text-[40px] 2xl:text-[45px] leading-[1.2] max-w-[340px] xl:max-w-[480px] mb-4 lg:mb-6">
              {subTitle}
            </div>
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 md:w-5 md:h-5 rounded-full bg-[#229CFF]" />
              <div className="w-4 h-4 md:w-5 md:h-5 rounded-full bg-[#ED943C]" />
              <div className="w-4 h-4 md:w-5 md:h-5 rounded-full bg-[#5B4DFF]" />
            </div>
          </div>

          {/* ── LINE 1: col 2, row 2-3 — extends to section bottom via ref ── */}
          <div
            style={{ gridColumn: '2', gridRow: '1 / 4', position: 'relative' }}
          >
            <div
              ref={line1Ref}
              className="absolute w-px bg-[#229CFF]/50 pointer-events-none"
              style={{ left: '50%', transform: 'translateX(-50%)' }}
            />
          </div>

          {/* ── LINE 2: col 4, full height — extends to section top+bottom via ref ── */}
          <div
            style={{ gridColumn: '4', gridRow: '1 / 4', position: 'relative' }}
          >
            <div
              ref={line2Ref}
              className="absolute w-px bg-[#229CFF]/50 pointer-events-none"
              style={{ left: '50%', transform: 'translateX(-50%)' }}
            />
          </div>

          {/* ── LINE 3: col 6, full height — extends to section top+bottom via ref ── */}
          <div
            style={{ gridColumn: '6', gridRow: '1 / 4', position: 'relative' }}
          >
            <div
              ref={line3Ref}
              className="absolute w-px bg-[#229CFF]/50 pointer-events-none"
              style={{ left: '50%', transform: 'translateX(-50%)' }}
            />
          </div>

          {/* ── CARDS ── */}
          <div style={{ gridColumn: '5', gridRow: '1' }}><Card item={industries[0]} /></div>
          <div style={{ gridColumn: '7', gridRow: '1' }}><Card item={industries[1]} /></div>
          <div style={{ gridColumn: '1', gridRow: '2' }}><Card item={industries[2]} /></div>
          <div style={{ gridColumn: '3', gridRow: '2' }}><Card item={industries[3]} /></div>
          <div style={{ gridColumn: '5', gridRow: '3' }}><Card item={industries[4]} /></div>
          <div style={{ gridColumn: '7', gridRow: '3' }}><Card item={industries[5]} /></div>
        </div>

        {/* ===================================================
            MOBILE — Centered layout + 2 columns
        =================================================== */}
        <div className="sm:hidden flex flex-col mt-2">
          <div className="flex flex-col items-center text-center px-2 mb-10">
            <div className="text-[#229CFF] text-xs tracking-[0.24em] uppercase font-semibold mb-2">
              {title}
            </div>
            <div className="font-medium text-white text-[22px] leading-[1.25] max-w-[290px] mb-4">
              {subTitle}
            </div>
            <div className="flex items-center gap-2.5">
              <div className="w-3 h-3 rounded-full bg-[#229CFF]" />
              <div className="w-3 h-3 rounded-full bg-[#ED943C]" />
              <div className="w-3 h-3 rounded-full bg-[#5B4DFF]" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3.5">
            {industries.map((item) => (
              <Card key={item.number} item={item} />
            ))}
          </div>
        </div>

      </div>
    </section>
  )
}

export default IndustriesSection